import React, { useRef, useState } from 'react';
import { ImagePlus, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, errMsg } from './api';
import { useCanEdit, useDeck } from './store';
import { SourceLine } from './SourceLine';

interface Props { sid: string; className?: string; imgClass?: string; dark?: boolean; frame?: string; srcSize?: number }

export function ImageSlot({ sid, className = '', imgClass = '', dark, frame = '', srcSize = 12 }: Props) {
  const { get, set, slug } = useDeck();
  const canEdit = useCanEdit();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const key = `${sid}:img`;
  const path: string = get(key) || '';
  if (!path && !canEdit) return null;

  const upload = async (f?: File) => {
    if (!f) return;
    setBusy(true);
    try {
      const r = await api.upload(slug, f);
      set(key, r.path);
      toast.success("Image ajoutée. N'oubliez pas sa source.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`} data-testid={`image-slot-${sid}`}>
      <div className={`group relative min-h-0 flex-1 overflow-hidden ${frame}`}>
        {path ? (
          <img src={api.fileUrl(path)} alt="" className={`h-full w-full object-contain ${imgClass}`} data-testid={`image-slot-${sid}-img`} />
        ) : (
          <button
            data-testid={`image-slot-${sid}-add`}
            onClick={() => input.current?.click()}
            className={`flex h-full w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed font-ui text-[15px] transition-colors ${dark ? 'border-white/25 text-white/50 hover:border-white/60 hover:text-white' : 'border-black/25 text-black/45 hover:border-black/60 hover:text-black'}`}
          >
            {busy ? <Loader2 className="animate-spin" /> : <ImagePlus />}
            Emplacement image (facultatif)
          </button>
        )}
        {path && canEdit && (
          <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <button data-testid={`image-slot-${sid}-replace`} onClick={() => input.current?.click()} className="rounded-full bg-black/80 p-2 text-white hover:bg-blue-600"><RefreshCw size={14} /></button>
            <button data-testid={`image-slot-${sid}-remove`} onClick={() => set(key, '')} className="rounded-full bg-black/80 p-2 text-white hover:bg-red-600"><Trash2 size={14} /></button>
          </div>
        )}
        <input ref={input} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`image-slot-${sid}-file`} />
      </div>
      {path && <SourceLine k={`${key}:src`} parentKey={key} testId={`image-src-${sid}`} label="Crédit" dark={dark} size={srcSize} />}
    </div>
  );
}
